import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Coffee, LogOut, LogIn, Timer, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { attendanceAPI } from '../../utils/api';

function formatMinutes(mins) {
  if (!mins && mins !== 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

function formatTime(iso) {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--';
  }
}

const STATUS_CONFIG = {
  not_started: { color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400', label: 'Not Clocked In' },
  active: { color: 'bg-emerald-50 text-emerald-700 border border-emerald-200', dot: 'bg-emerald-500', label: 'Active' },
  on_break: { color: 'bg-amber-50 text-amber-700 border border-amber-200', dot: 'bg-amber-500', label: 'On Break' },
  punched_out: { color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400', label: 'Clocked Out' },
};

export default function AttendanceWidget() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const fetchSession = useCallback(async () => {
    try {
      const { data } = await attendanceAPI.getToday();
      setSession(data);
      setElapsed(data.log?.elapsed_minutes || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Live timer
  useEffect(() => {
    if (session?.status !== 'active' && session?.status !== 'on_break') return;
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, [session?.status]);

  const handlePunchIn = async () => {
    setActing(true);
    try {
      await attendanceAPI.punchIn();
      toast.success('Clocked in! Have a productive day.');
      await fetchSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock in');
    } finally {
      setActing(false);
    }
  };

  const handlePunchOut = async () => {
    setActing(true);
    try {
      await attendanceAPI.punchOut();
      toast.success('Clocked out. Great work today!');
      await fetchSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock out');
    } finally {
      setActing(false);
    }
  };

  const handleBreakStart = async () => {
    setActing(true);
    try {
      await attendanceAPI.breakStart();
      toast.info('Break started. Enjoy your break!');
      await fetchSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start break');
    } finally {
      setActing(false);
    }
  };

  const handleBreakEnd = async () => {
    setActing(true);
    try {
      await attendanceAPI.breakEnd();
      toast.success('Welcome back! Break ended.');
      await fetchSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end break');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-5 bg-slate-100 rounded w-32 mb-3" />
        <div className="h-8 bg-slate-100 rounded w-48" />
      </div>
    );
  }

  const status = session?.status || 'not_started';
  const log = session?.log;
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;

  return (
    <div className={`rounded-xl border p-4 flex items-center gap-4 flex-wrap ${cfg.color}`}>
      {/* Status dot + label */}
      <div className="flex items-center gap-2 min-w-[140px]">
        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${status === 'active' ? 'animate-pulse' : ''}`} />
        <span className="font-semibold text-sm">{cfg.label}</span>
      </div>

      {/* Time info */}
      {log && (
        <div className="flex items-center gap-4 text-sm">
          {log.punch_in_time && (
            <div className="flex items-center gap-1 opacity-80">
              <LogIn className="w-3.5 h-3.5" />
              <span>In: {formatTime(log.punch_in_time)}</span>
            </div>
          )}
          {(status === 'active' || status === 'on_break') && (
            <div className="flex items-center gap-1 font-mono font-semibold">
              <Timer className="w-3.5 h-3.5" />
              <span>{formatMinutes(elapsed)}</span>
            </div>
          )}
          {log.punch_out_time && (
            <div className="flex items-center gap-1 opacity-80">
              <LogOut className="w-3.5 h-3.5" />
              <span>Out: {formatTime(log.punch_out_time)}</span>
            </div>
          )}
          {status === 'punched_out' && (
            <div className="flex items-center gap-1 font-semibold">
              <Clock className="w-3.5 h-3.5" />
              <span>Total: {formatMinutes(log.total_work_minutes)}</span>
            </div>
          )}
          {log.total_break_minutes > 0 && (
            <div className="flex items-center gap-1 opacity-70">
              <Coffee className="w-3.5 h-3.5" />
              <span>Break: {formatMinutes(log.total_break_minutes)}</span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="ml-auto flex gap-2">
        {status === 'not_started' && (
          <Button size="sm" onClick={handlePunchIn} disabled={acting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
            <LogIn className="w-3.5 h-3.5" /> Clock In
          </Button>
        )}
        {status === 'active' && (
          <>
            <Button size="sm" variant="outline" onClick={handleBreakStart} disabled={acting}
              className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50">
              <Coffee className="w-3.5 h-3.5" /> Break
            </Button>
            <Button size="sm" onClick={handlePunchOut} disabled={acting}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Clock Out
            </Button>
          </>
        )}
        {status === 'on_break' && (
          <>
            <Button size="sm" onClick={handleBreakEnd} disabled={acting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
              <Play className="w-3.5 h-3.5" /> Resume
            </Button>
            <Button size="sm" onClick={handlePunchOut} disabled={acting}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> Clock Out
            </Button>
          </>
        )}
        {status === 'punched_out' && (
          <Button size="sm" onClick={handlePunchIn} disabled={acting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
            <LogIn className="w-3.5 h-3.5" /> Clock In
          </Button>
        )}
      </div>
    </div>
  );
}
